<template>
  <div class="flex flex-col flex-1 min-h-0 min-w-0 w-full border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] rounded-lg overflow-hidden">
    <div
      class="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-3"
      ref="scrollableMessages"
    >
      <div
        v-for="(msg, index) in messages"
        :key="messageKey(msg, index)"
        :class="[
          'mb-2 flex w-full min-w-0 pb-4',
          isFromUser(msg) ? 'justify-end' : 'justify-start'
        ]"
      >
        <div
          :class="[
            'message-bubble p-3 rounded-lg w-fit max-w-full sm:max-w-[85%] min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere] relative',
            isFromUser(msg)
              ? 'bg-white text-[#3A5762] border border-gray-300 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]'
              : 'bg-white text-[#3A5762] border border-gray-300 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]'
          ]"
        >
          <!-- Tres puntos verticales con menú desplegable para mensajes del usuario -->
          <div v-if="canManageMessage(msg)" class="absolute top-2 right-2 flex flex-col items-end">
            <span @click="toggleMenu(index)" class="text-xl pt-4 text-gray-400 cursor-pointer select-none">⋮</span>
            <div v-if="openMenuIndex === index" class="mt-2 bg-white border rounded shadow-md z-20 min-w-[80px] msg-menu-dropdown">
              <button @click="openEditModal(msg)" class="block w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer">{{ $t('dashboard.buttons.edit') || 'Editar' }} </button>
              <button
                v-if="canDeleteMessage(msg)"
                @click="deleteMessage(msg)"
                class="block w-full text-left px-4 py-2 hover:bg-gray-100 text-red-600 cursor-pointer"
              >
                {{ $t('dashboard.buttons.delete') || 'Eliminar' }}
              </button>
            </div>
          </div>
            <!-- Modal edición nota -->
            <div v-if="showEditModal && !props.isReadOnly" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
                <button @click="closeEditModal" class="absolute top-2 right-2 text-gray-400 text-xl cursor-pointer">&times;</button>
                <h3 class="text-lg font-bold mb-4">{{ $t('notifications.note.edit') || 'Editar nota' }}</h3>
                <textarea v-model="editModalText" rows="4" class="w-full border border-[#7CADB9] rounded px-2 py-1 mb-4 text-black"></textarea>
                <div class="flex justify-end gap-2">
                  <button @click="closeEditModal" class="px-4 py-2 bg-gray-300 rounded hover:bg-gray-200 cursor-pointer ">{{ $t('formSubmit.buttons.cancel') }}</button>
                  <button @click="submitEditModal" class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer ">{{  $t('formSubmit.buttons.submit') }}</button>
                </div>
              </div>
            </div>
          <div class="flex items-center mb-4 min-w-0" :class="[
            isFromUser(msg) ? 'justify-end' : 'justify-start',
            props.matchNotificationStyle || (!props.isNotes && !props.isInternal)
              ? 'notification-heading'
              : 'text-xs text-[#2AB4D9]/70'
          ]">
            <div class="flex flex-wrap justify-end items-center gap-x-2 min-w-0">
              <span class="font-bold min-w-0 break-words">
                {{ messageAuthor(msg) }}
              </span>
              <span class="shrink-0">{{ formatDate(msg.created_at) }}</span>
            </div>
          </div>
          <div
            v-if="msg.scheduled_date"
            class="scheduled-date flex items-center text-black mb-3 -mt-2"
            :class="isFromUser(msg) ? 'justify-end' : 'justify-start'"
          >
            <span>{{ $t('notifications.note.scheduledDate') || 'Fecha programada' }}: {{ formatScheduledDate(msg.scheduled_date) }}</span>
          </div>
          <div :class="{ 'notification-content': props.matchNotificationStyle || (!props.isNotes && !props.isInternal) }">
            <div v-if="msg.message || msg.content" class="mb-2">
              {{ displayMessage(msg) }}
            </div>
            <div v-if="msg.observations" class="mb-2">
              <strong>Observaciones:</strong> {{ msg.observations }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="p-3 border-t flex gap-2 flex-col">
      <div v-if="showCalendar" class="">
        <CalendarIcon class="w-2 h-5 text-[#385660] mr-2" />
        <input type="date" v-model="selectedRoadmapDate" :disabled="props.isReadOnly || isSending" class="border border-gray-300 rounded-md px-0 py-2 text-[#385660] bg-white min-w-0 disabled:bg-gray-100 disabled:text-gray-400" />
      </div>
      <div class="flex items-end gap-2 min-w-0">
        <!-- Enter pasa de línea: el mensaje solo se envía con el botón (ADSREC-0013). -->
        <textarea
          ref="messageInput"
          rows="1"
          v-model="newMessageText"
          :placeholder="props.isReadOnly ? ($t('notifications.note.readOnly') || 'Solicitud cerrada: no se pueden escribir mensajes') : 'Escribe tu mensaje...'"
          :disabled="props.isReadOnly || isSending"
          class="flex-1 min-w-0 px-3 py-2 border border-[#7CADB9] rounded-md text-black leading-6 max-h-[162px] resize-none overflow-y-auto placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
        ></textarea>
        <button
          @click="sendMessage"
          :disabled="props.isReadOnly || isSending"
          class="p-2 bg-[var(--c-primary)] hover:bg-[var(--c-primary2)] cursor-pointer text-white rounded-md flex items-center justify-center"
          :class="{ 'opacity-50 cursor-not-allowed hover:bg-[var(--c-primary)]': props.isReadOnly || isSending }"
          style="min-width: 40px; min-height: 40px;"
        >
          <img src="@/assets/img/ArrowSeen.svg" alt="Enviar" class="h-5 w-5" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.notification-heading {
  font-size: var(--readable-label-size);
  color: var(--readable-label-color);
}

/* El reset global fija font-weight: normal también en los elementos hijos. */
.notification-heading,
.notification-heading span {
  font-weight: var(--readable-label-weight);
}

.notification-content {
  color: #000000;
}

/* La fecha programada de la Hoja de ruta tiene que destacar (ADSREC-0010). */
.scheduled-date,
.scheduled-date span {
  font-weight: 700;
}
</style>

<script setup>
// --- impoerts ---
import { ref as vueRef } from 'vue';
const selectedRoadmapDate = vueRef('');

import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { hasAnyRole } from '@/services/userRoles';
import CalendarIcon from '@/assets/svg/CalendarIcon.vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
import axios from 'axios';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';
import { deduplicatePersistedItems } from '@/services/requestConversation';

// props
const props = defineProps({
  message: {
    type: Array,
    required: true,
    default: () => []
  },
  request_id: {
    type: [String, Number],
    required: true
  },
  sentToId: {
    type: Number,
    default: null
  },
  isNotes: {
    type: Boolean,
    default: false
  },
  isInternal: {
    type: Boolean,
    default: false
  },
  showCalendar: {
    type: Boolean,
    default: false
  },
  isReadOnly: {
    type: Boolean,
    default: false
  },
  matchNotificationStyle: {
    type: Boolean,
    default: false
  }
});

// data and methods
const localMessages = ref([]);
const deletedMessageIds = ref(new Set());
const sortMessagesChronologically = (items) => {
  return [...items].sort((a, b) => {
    const aTime = new Date(a.created_at || a.date || 0).getTime();
    const bTime = new Date(b.created_at || b.date || 0).getTime();
    if (aTime !== bTime) return aTime - bTime;
    return Number(a.id || 0) - Number(b.id || 0);
  });
};
const messages = computed(() => {
  const merged = deduplicatePersistedItems(localMessages.value, props.message)
    .filter(msg =>
      Number(msg.request_id) === Number(props.request_id)
      && !deletedMessageIds.value.has(msg.id)
    );

  if (!props.isNotes || props.isInternal) {
    return sortMessagesChronologically(merged);
  }

  return merged;
});
const messageKey = (message, index) => message?.id === null || message?.id === undefined
  ? `transient-${index}`
  : `persisted-${message.id}`;
const userId = Number(localStorage.getItem('id'));
const yourName = localStorage.getItem('name');
const isAdminRole = hasAnyRole(['admin', 'superadmin']);

const newMessageText = ref('');
const isSending = ref(false);
const scrollableMessages = ref(null);
const messageInput = ref(null);

// El área de escritura crece con el texto hasta 6 líneas (max-h-[162px]);
// a partir de ahí aparece el scroll. También vuelve a una línea al enviar.
const resizeMessageInput = () => {
  const el = messageInput.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
};
watch(newMessageText, () => nextTick(resizeMessageInput));

// Funciones auxiliares
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
};

const formatScheduledDate = (dateString) => {
  if (!dateString) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }
  return new Date(dateString).toLocaleDateString();
};

const messageAuthor = (msg) => {
  if (isFromUser(msg)) {
    return msg.sender?.name || msg.user?.name || msg.contact_name || yourName || 'Tú';
  }

  return msg.sender?.name || msg.user?.name || msg.contact_name || 'SISTEMA';
};

const displayMessage = (msg) => {
  const text = msg.message || msg.content || '';
  if (!props.isInternal || typeof text !== 'string') {
    return text;
  }

  return text
    .replace(/^\[\s*Legacy\s+Nota\s*-\s*([^\]]+)\]\s*/iu, '$1: ')
    .replace(/^\[\s*Legacy\s+([^\]]+)\]\s*/iu, '$1: ')
    .replace(/^\s*Legacy\s*[-:]?\s*/iu, '');
};

const scrollToBottom = () => {
    setTimeout(() => {
        if (scrollableMessages.value) {
            scrollableMessages.value.scrollTop = scrollableMessages.value.scrollHeight;
        }
    }, 50);
};

// Enviar mensaje o nota
const sendMessage = async () => {
  if (props.isReadOnly || isSending.value) return;
  // Sin las líneas en blanco del principio y del final; los saltos del medio se conservan.
  const messageText = newMessageText.value.trim();
  const hasMessage = messageText !== '';
  const hasDate = props.showCalendar && selectedRoadmapDate.value;
  if (hasMessage || hasDate) {
        isSending.value = true;
        try {
            const requestId = Number(props.request_id);
            const userId = parseInt(localStorage.getItem('id'));

            if (isNaN(requestId)) {
                throw new Error('Invalid request_id');
            }

            if (props.isNotes) {
                // Handle notes creation
        // Construir el cuerpo de la petición
        const notePayload = {
          title: "Nota de seguimiento",
          content: messageText,
          user_id: userId,
          request_id: requestId
        };
        if (selectedRoadmapDate.value) {
          notePayload.scheduled_date = selectedRoadmapDate.value;
        }
        const endpoint = props.isInternal
          ? `${import.meta.env.VITE_API_URL}/request/internal-notes/create`
          : `${import.meta.env.VITE_API_URL}/request/notes/create`;

        const response = await axios.post(
          endpoint,
          notePayload,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          }
        );

                if (response.data?.value) {
                    const created = response.data.value;
                    localMessages.value.push({
                        id: created.id,
                        contact_name: yourName,
                        user: created.user || { id: userId, name: yourName },
                        user_id: userId,
                        request_id: requestId,
                        created_at: created.created_at || new Date().toISOString(),
                        content: created.content || messageText
                    });
                    newMessageText.value = '';
                    selectedRoadmapDate.value = '';
                    scrollToBottom();
                }
            } else {
                // Handle notifications (existing code)
                const response = await axios.post(
                    `${import.meta.env.VITE_API_URL}/notification/create`,
                    {
                        title: "Nueva notificación",
                        message: messageText,
                        from_id: userId,
                        sent_to_id: props.sentToId,
                        request_id: requestId,
                        date: new Date().toISOString().split('T')[0],
                        type: "info"
                    },
                    {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        }
                    }
                );

                if (response.data?.value) {
                    const created = response.data.value;
                    localMessages.value.push({
                        id: created.id,
                        contact_name: 'You',
                        from_id: userId,
                        request_id: requestId,
                        created_at: created.created_at || new Date().toISOString(),
                        message: created.message || messageText
                    });
                    newMessageText.value = '';
                    scrollToBottom();
                }
            }
        } catch (error) {
            console.error('Error al enviar el mensaje:', error);
            console.error('Error response:', error.response?.data);
            toast.error(error.response?.data?.message || 'Error al enviar el mensaje', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        } finally {
            isSending.value = false;
        }
    }
};

// Función para determinar si el mensaje es del usuario actual
const isFromUser = (msg) => {
  // Para notas: compara user_id con el id actual
  if (props.isNotes) {
    return Number(msg.user_id) === userId;
  }
  // Para mensajes: puedes mantener tu lógica previa
  return msg.contact_name === 'You' || Number(msg.from_id) === userId;
};

const isLegacyInternalNote = (msg) => {
  return props.isInternal && typeof msg.content === 'string' && msg.content.startsWith('[Legacy ');
};

const canManageMessage = (msg) => {
  return !props.isReadOnly && !isLegacyInternalNote(msg) && isFromUser(msg) && isAdminRole;
};

const canDeleteMessage = (msg) => {
  return props.isNotes && canManageMessage(msg);
};

const removeLocalMessage = (id) => {
  const idx = localMessages.value.findIndex(m => m.id === id);
  if (idx !== -1) {
    localMessages.value.splice(idx, 1);
  }
  deletedMessageIds.value = new Set([...deletedMessageIds.value, id]);
};


// abrir desplegable de opciones al hacer clic en los tres puntos
import { onBeforeUnmount } from 'vue';
const openMenuIndex = ref(null);
const showEditModal = ref(false);
const editModalText = ref('');
const editModalId = ref(null);

const toggleMenu = (idx) => {
  openMenuIndex.value = openMenuIndex.value === idx ? null : idx;
};

const handleClickOutside = (event) => {
  const menus = document.querySelectorAll('.msg-menu-dropdown');
  let clickedInside = false;
  menus.forEach(menu => {
    if (menu.contains(event.target)) clickedInside = true;
  });
  if (!clickedInside) {
    openMenuIndex.value = null;
  }
};

const addClickOutsideListener = () => {
  document.addEventListener('mousedown', handleClickOutside);
};
const removeClickOutsideListener = () => {
  document.removeEventListener('mousedown', handleClickOutside);
};

onMounted(() => {
  scrollToBottom();
  addClickOutsideListener();
});
onBeforeUnmount(() => {
  removeClickOutsideListener();
});


const editModalType = ref('note'); // 'note' o 'notification'
const openEditModal = (msg) => {
  if (!canManageMessage(msg)) return;
  openMenuIndex.value = null;
  editModalText.value = msg.content || msg.message || '';
  editModalId.value = msg.id;
  editModalType.value = props.isInternal ? 'internal-note' : (props.isNotes ? 'note' : 'notification');
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  editModalText.value = '';
  editModalId.value = null;
};

const submitEditModal = async () => {
  if (props.isReadOnly || !editModalText.value.trim() || !editModalId.value) return;
  try {
    let response;
    if (editModalType.value === 'internal-note') {
      response = await axios.put(
        `${import.meta.env.VITE_API_URL}/request/internal-notes/update/${editModalId.value}`,
        { content: editModalText.value },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
    } else if (editModalType.value === 'note') {
      response = await axios.put(
        `${import.meta.env.VITE_API_URL}/request/notes/update/${editModalId.value}`,
        { content: editModalText.value },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
    } else {
      response = await axios.put(
        `${import.meta.env.VITE_API_URL}/notification/update/${editModalId.value}`,
        { message: editModalText.value },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
    }
    if (response.data?.value) {
      // Actualizar el mensaje en la lista local
      const msg = messages.value.find(m => m.id === editModalId.value);
      if (editModalType.value === 'note' || editModalType.value === 'internal-note') {
        if (msg) msg.content = editModalText.value;
      } else {
        if (msg) msg.message = editModalText.value;
      }
      closeEditModal();
      scrollToBottom();
      toast.success('Actualizado', { autoClose: 2000 });
    }
  } catch (error) {
    toast.error(error.response?.data?.message || 'Error al actualizar');
  }
};

const deleteMessage = async (msg) => {
  if (!canDeleteMessage(msg) || !msg?.id) return;

  const confirmed = window.confirm(t('dashboard.buttons.confirmDelete') || '¿Seguro que quieres eliminar esta nota?');
  if (!confirmed) return;

  try {
    const endpoint = props.isInternal
      ? `${import.meta.env.VITE_API_URL}/request/internal-notes/delete/${msg.id}`
      : `${import.meta.env.VITE_API_URL}/request/notes/delete/${msg.id}`;

    const response = await axios.delete(endpoint, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Accept': 'application/json'
      }
    });

    if (response.data?.value) {
      removeLocalMessage(msg.id);
      openMenuIndex.value = null;
      toast.success(t('dashboard.buttons.deleted') || 'Nota eliminada', { autoClose: 2000 });
    }
  } catch (error) {
    toast.error(error.response?.data?.value || error.response?.data?.message || 'Error al eliminar la nota');
  }
};



// Scroll to bottom on mount
onMounted(() => {
    scrollToBottom();
});
</script>
